import { Button, Card, DatePicker, Divider, Flex, Segmented, Select, Table, TableProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// components
import Icon from 'utils/icon';
import StatusComponent from 'components/status/status';
import NoData from 'components/noData/noData';

// models
import { SharesTransaction, SharesTransactionsPayload, TransactionStatus } from '../models/transaction';
import { NoUndefinedRangeValueType } from 'rc-picker/lib/PickerInput/RangePicker';
import NotificationService from 'core/services/notification';
import { onExportSharesTransactions, onGetSharesTransactions, onGetSharesTransactionsFilter } from '../services/transactionsService';
import { useLocation, useNavigate } from 'react-router';
import TableTotal from 'components/tableTotal/tableTotal';
import NumberElement from 'components/number/number';
import { isRTL } from 'core/services/language';
import { StatusColors } from 'utils/statusColors';
import { downloadFile } from 'utils/downloadFile';
import PageTitle from 'components/title/title';
import { TranslatedField } from 'core/models/common';
import ListButton from 'components/listButton/listButton';

// components
import SuspendedTooltip from 'components/suspendedTooltip/suspendedTooltip';

interface TransactionsLogsComponentProps {}

const TransactionsLogsComponent: FunctionComponent<TransactionsLogsComponentProps> = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const { RangePicker } = DatePicker;

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isDownloading, setIsDownloading] = useState<boolean>(false);

	const [dateRange, setDateRange] = useState<[string, string]>();
	const [status, setStatus] = useState<TransactionStatus | ''>('');
	const [transactionType, setTransactionType] = useState<string>('');

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const [transactions, setTransactions] = useState<SharesTransaction[]>([]);

	const [transactionTypeOptions, setTransactionTypeOptions] = useState<{ value: string; translated_value: TranslatedField }[]>([]);

	const statusOptions = [
		{ value: '', label: t('COMMON.ALL') },
		{ value: 'Pending', label: t('COMMON.PENDING') },
		{ value: 'Rejected', label: t('COMMON.REJECTED') },
		{ value: 'Canceled', label: t('COMMON.CANCELED') },
		{ value: 'Expired', label: t('COMMON.EXPIRED') },
		{ value: 'Completed', label: t('COMMON.COMPLETED') },
		{ value: 'Reversed', label: t('COMMON.REVERSED') },
	];

	const columns: TableProps<SharesTransaction>['columns'] = [
		{
			title: t('ACTIONS.REQUEST_DATE'),
			dataIndex: 'created_at',
			key: 'created_at',
			render: (date) => <span dir="ltr">{dayjs(date).format('DD/MM/YYYY, hh:mm A')}</span>,
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status',
			key: 'status',
			render: (status) => <StatusComponent text={t(`COMMON.${status.toUpperCase()}`)} color={StatusColors[status]} />,
		},
		{
			title: t('ACTIONS.SELLER_NATIONAL_ID'),
			dataIndex: 'source',
			key: 'source',
			render: (source) => source?.national_id,
		},
		{
			title: t('ACTIONS.SELLER_NAME'),
			dataIndex: 'source',
			key: 'source2',
			render: (source) => isRTL() ? source?.translated_name?.ar : source?.translated_name?.en,
		},
		{
			title: t('ACTIONS.BUYER_NATIONAL_ID'),
			dataIndex: 'destination',
			key: 'destination',
			render: (destination) => destination?.national_id,
		},
		{
			title: t('ACTIONS.BUYER_NAME'),
			dataIndex: 'destination',
			key: 'destination2',
			render: (destination) => isRTL() ? destination?.translated_name?.ar : destination?.translated_name?.en,
		},
		{
			title: t('COMMON.NUM_OF_SHARES'),
			dataIndex: 'shares_count',
			key: 'shares_count',
		},
		{
			title: t('COMMON.SHARE_PRICE'),
			dataIndex: 'share_price',
			key: 'share_price',
			render: (price) => <NumberElement value={price} decimalScale={2} />,
		},
		{
			title: t('COMMON.TRANSACTION_TYPE'),
			dataIndex: 'translated_transaction_type',
			key: 'translated_transaction_type',
			render: (transactionType) => (isRTL() ? transactionType?.ar : transactionType?.en),
		},
		{
			title: t('COMMON.CONTRACT'),
			dataIndex: '_',
			key: 'contract',
			render: (_, row) =>
				row.is_contract_downloadable ? (
					<Flex justify="center">
						{isRTL() && row.arabic_contract && row.arabic_contract.download_link && (
							<ListButton type="download" onClick={() => downloadFile(row.arabic_contract?.download_link!, row.arabic_contract?.title!)} />
						)}
						{!isRTL() && row.english_contract && row.english_contract.download_link && (
							<ListButton type="download" onClick={() => downloadFile(row.english_contract?.download_link!, row.english_contract?.title!)} />
						)}
					</Flex>
				) : (
					t('COMMON.N_A')
				),
		},
	];

	useEffect(() => {
		getTransactionsTypeOptions();
	}, []);

	useEffect(() => {
		setCurrentPage(getCurrentPage());

		getTransactionLogs();
	}, [status, transactionType, dateRange]);

	const getCurrentPage = () => {
		const params = new URLSearchParams(location.search);
		const page = params.get('page');
		return page ? parseInt(page) : 1;
	};

	const onDateRangeChange = (dates: NoUndefinedRangeValueType<Dayjs> | null) => {
		if (dates && dates[0] && dates[1]) {
			setDateRange([dates[0].format(), dates[1].format()]);
		} else {
			setDateRange(undefined);
		}
	};

	const getTransactionLogs = async () => {
		try {
			setIsLoading(true);

			const payload: SharesTransactionsPayload = {
				page: getCurrentPage(),
				status: status || undefined,
				transaction_type: transactionType || undefined,
				date_from: dateRange ? dayjs(dateRange[0]).format('YYYY-MM-DD') : undefined,
				date_to: dateRange ? dayjs(dateRange[1]).format('YYYY-MM-DD') : undefined,
			};

			const response = await onGetSharesTransactions(payload);

			setTransactions(response.share_transactions);
			setTotalRecords(response.pagination.total);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const getTransactionsTypeOptions = async () => {
		try {
			const response = await onGetSharesTransactionsFilter();

			setTransactionTypeOptions(response.transaction_type);
		} catch (error) {}
	};

	const fetchMoreData = async (page: number) => {
		try {
			const params = new URLSearchParams(location.search);
			params.set('page', page.toString());
			navigate(`${location.pathname}?${params.toString()}`, { replace: true });

			setIsLoading(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });

			const payload: SharesTransactionsPayload = {
				page: page,
				status: status || undefined,
				transaction_type: transactionType || undefined,
				date_from: dateRange ? dayjs(dateRange[0]).format('YYYY-MM-DD') : undefined,
				date_to: dateRange ? dayjs(dateRange[1]).format('YYYY-MM-DD') : undefined,
			};

			const response = await onGetSharesTransactions(payload);
			setTransactions(response['share_transactions']);

			setCurrentPage(page);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoading(false);
		}
	};

	const onExportTransactionsHandler = async () => {
		try {
			setIsDownloading(true);

			const payload: SharesTransactionsPayload = {
				page: getCurrentPage(),
				status: status || undefined,
				date_from: dateRange ? dayjs(dateRange[0]).format('YYYY-MM-DD') : undefined,
				date_to: dateRange ? dayjs(dateRange[1]).format('YYYY-MM-DD') : undefined,
			};

			const exportResponse = await onExportSharesTransactions(payload);

			if (exportResponse === null) {
				NotificationService.error(t('COMMON.ERROR'), t('COMMON.DOWNLOAD_ERROR'));
				setIsDownloading(false);
				return;
			}

			await downloadFile(exportResponse!.download_url, isRTL() ? 'قائمة المساهمين.xlsx' : 'shareholders-list.xlsx');

			setIsDownloading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.EXPORT_ERROR'));
			setIsDownloading(false);
		}
	};

	return (
		<>
			<PageTitle name={t('COMPANY_INFORMATION.TRANSACTIONS_LOGS')} />

			<Card className="mt-4">
				<Flex justify="space-between" align="center" gap={20} wrap>
					<Flex justify="space-between" align="center" gap={20} wrap>
						<div>
							<p className="text-light fw-medium mb-1">{t('COMMON.DATE_RANGE')}</p>
							<RangePicker
								size="large"
								maxDate={dayjs(new Date())}
								format="DD MMM YYYY"
								suffixIcon={<Icon name="calendar" />}
								separator="-"
								inputReadOnly
								placeholder={[t('COMMON.START_DATE'), t('COMMON.END_DATE')]}
								onChange={onDateRangeChange}
							/>
						</div>

						<div>
							<p className="text-light fw-medium mb-1">{t('COMMON.STATUS')}</p>
							<Select size="large" onChange={setStatus} options={statusOptions} defaultValue={''} className="filled" />
						</div>

						<div>
							<p className="text-light fw-medium mb-1">{t('COMMON.TRANSACTION_TYPE')}</p>
							<Select
								size="large"
								onChange={setTransactionType}
								options={[
									{ value: '', label: t('COMMON.ALL') },
									...transactionTypeOptions.map((type) => ({
										value: type.value,
										label: isRTL() ? type.translated_value.ar : type.translated_value.en,
									})),
								]}
								defaultValue={''}
								className="filled moreWidth"
							/>
						</div>
					</Flex>

					<SuspendedTooltip>
						<Button type="primary" size="middle" disabled={isDownloading} loading={isDownloading} onClick={onExportTransactionsHandler}>
							{t('COMMON.EXPORT_TO_EXCEL')}
						</Button>
					</SuspendedTooltip>
				</Flex>

				<Divider />

				<Table
					columns={columns}
					dataSource={transactions}
					scroll={{ x: 600 }}
					rowKey="id"
					loading={isLoading}
					pagination={{
						pageSize: 25,
						total: totalRecords,
						showSizeChanger: false,
						current: currentPage,
						onChange: fetchMoreData,
						showTotal: (total) => <TableTotal total={total} />,
					}}
					locale={{ emptyText: <NoData /> }}
				/>
			</Card>
		</>
	);
};

export default TransactionsLogsComponent;
